import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBreakpoint } from '../hooks/useBreakpoint';

const SLIDES = [
  {
    id: 1,
    bg: 'linear-gradient(135deg, #8B1A1A 0%, #6B0F0F 45%, #C4891A 100%)',
    overlay: 'radial-gradient(ellipse at 70% 50%, rgba(212,160,23,0.15) 0%, transparent 60%)',
    eyebrow: 'Handcrafted by Indian Artisans',
    title: 'Handcrafted Indian Style\nfor Every Occasion',
    subtitle: 'Authentic Punjabi Juttis, Kundan Jewellery & Ethnic Accessories. Free shipping above ₹999.',
    cta: { label: 'Shop Best Sellers', to: '/shop?is_best_seller=true' },
    ctaSecondary: { label: 'View Juttis', to: '/shop?category=juttis' },
    // Replace src with your actual banner image URL
    imageSrc: null,
    imageAlt: 'A1 Looks Handcrafted Collection',
  },
  {
    id: 2,
    bg: 'linear-gradient(120deg, #3D0C0C 0%, #7A1515 50%, #8B1A1A 100%)',
    overlay: 'radial-gradient(ellipse at 30% 60%, rgba(139,26,26,0.4) 0%, transparent 70%)',
    eyebrow: 'Step Into Tradition',
    title: 'Punjabi Juttis\nfor Every Occasion',
    subtitle: 'Handstitched by skilled artisans. Every pair tells a story of craft and culture.',
    cta: { label: 'Shop Juttis', to: '/shop?category=juttis' },
    ctaSecondary: { label: 'Under ₹499', to: '/shop?max_price=499' },
    imageSrc: null,
    imageAlt: 'Punjabi Juttis Collection',
  },
  {
    id: 3,
    bg: 'linear-gradient(135deg, #1A1035 0%, #5C1C4A 50%, #8B1A1A 100%)',
    overlay: 'radial-gradient(ellipse at 65% 40%, rgba(212,160,23,0.2) 0%, transparent 55%)',
    eyebrow: 'New Arrivals — Just Landed',
    title: 'Kundan Jewellery\nfor Every Look',
    subtitle: 'Earrings, Necklaces, Bracelets & Rings. Handcrafted with Kundan & crystal stones.',
    cta: { label: 'Shop Jewellery', to: '/shop?category=earrings' },
    ctaSecondary: { label: 'New Arrivals', to: '/shop?is_new_arrival=true' },
    imageSrc: null,
    imageAlt: 'Kundan Jewellery Collection',
  },
  {
    id: 4,
    bg: 'linear-gradient(135deg, #2C1A0E 0%, #6B3010 40%, #8B1A1A 80%, #C4891A 100%)',
    overlay: 'radial-gradient(ellipse at 50% 30%, rgba(212,160,23,0.25) 0%, transparent 60%)',
    eyebrow: 'Wedding Season',
    title: 'Complete Your\nBridal Look',
    subtitle: 'Jutti + Earrings + Potli — Wedding Combos starting from ₹1,399. Save ₹398.',
    cta: { label: 'Shop Wedding Combos', to: '/shop?category=wedding-collection' },
    ctaSecondary: { label: 'View Potli Bags', to: '/shop?category=potli-bags' },
    imageSrc: null,
    imageAlt: 'Wedding Bridal Collection',
  },
];

const AUTOPLAY_MS = 4500;

export default function HeroBanner() {
  const [current, setCurrent] = useState(0);
  const [paused, setPaused] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const touchStartX = useRef(null);
  const count = SLIDES.length;

  const goTo = useCallback((idx) => {
    if (transitioning) return;
    setTransitioning(true);
    setCurrent(idx);
    setTimeout(() => setTransitioning(false), 650);
  }, [transitioning]);

  const next = useCallback(() => goTo((current + 1) % count), [current, count, goTo]);
  const prev = useCallback(() => goTo((current - 1 + count) % count), [current, count, goTo]);

  useEffect(() => {
    if (paused) return;
    const id = setInterval(next, AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [paused, next]);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 48) diff > 0 ? next() : prev();
    touchStartX.current = null;
  };

  const slide = SLIDES[current];
  const height = isMobile ? 380 : 520;

  return (
    <div
      style={{ position: 'relative', overflow: 'hidden', userSelect: 'none' }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Slide strip */}
      <div
        style={{
          display: 'flex',
          width: `${count * 100}%`,
          transform: `translateX(-${current * (100 / count)}%)`,
          transition: 'transform 0.65s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
          willChange: 'transform',
        }}
      >
        {SLIDES.map((s, i) => (
          <div
            key={s.id}
            style={{
              width: `${100 / count}%`,
              flexShrink: 0,
              position: 'relative',
              background: s.bg,
              minHeight: height,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              padding: isMobile ? '52px 20px 72px' : '64px 24px 80px',
              overflow: 'hidden',
            }}
          >
            {/* Colour overlay for depth */}
            <div style={{
              position: 'absolute', inset: 0,
              background: s.overlay,
              pointerEvents: 'none',
            }} />

            {/* Optional product photo background */}
            {s.imageSrc && (
              <div style={{
                position: 'absolute', inset: 0,
                backgroundImage: `url(${s.imageSrc})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                opacity: 0.35,
              }} />
            )}

            {/* Subtle decorative circle */}
            <div style={{
              position: 'absolute',
              width: isMobile ? 220 : 460,
              height: isMobile ? 220 : 460,
              borderRadius: '50%',
              border: '1px solid rgba(255,255,255,0.07)',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
            }} />
            <div style={{
              position: 'absolute',
              width: isMobile ? 320 : 680,
              height: isMobile ? 320 : 680,
              borderRadius: '50%',
              border: '1px solid rgba(255,255,255,0.04)',
              top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              pointerEvents: 'none',
            }} />

            {/* Slide content */}
            <div style={{ position: 'relative', zIndex: 1, maxWidth: 680, width: '100%' }}>
              <p style={{
                color: '#D4A017',
                fontSize: isMobile ? 11 : 13,
                letterSpacing: 3,
                textTransform: 'uppercase',
                marginBottom: 14,
                fontWeight: 600,
              }}>
                {s.eyebrow}
              </p>

              <h2 style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: isMobile ? 'clamp(26px, 7vw, 38px)' : 'clamp(34px, 5vw, 60px)',
                color: '#fff',
                lineHeight: 1.2,
                marginBottom: 16,
                textShadow: '0 2px 12px rgba(0,0,0,0.4)',
                whiteSpace: 'pre-line',
              }}>
                {s.title}
              </h2>

              <p style={{
                color: 'rgba(255,255,255,0.82)',
                fontSize: isMobile ? 13 : 16,
                lineHeight: 1.7,
                maxWidth: 460,
                margin: `0 auto ${isMobile ? 28 : 36}px`,
              }}>
                {s.subtitle}
              </p>

              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={() => navigate(s.cta.to)}
                  style={{
                    background: '#fff',
                    color: '#8B1A1A',
                    border: 'none',
                    padding: isMobile ? '11px 24px' : '13px 34px',
                    fontSize: isMobile ? 13 : 15,
                    fontWeight: 700,
                    borderRadius: 4,
                    cursor: 'pointer',
                    letterSpacing: 0.5,
                    transition: 'background 0.2s, color 0.2s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#D4A017'; e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.color = '#8B1A1A'; }}
                >
                  {s.cta.label}
                </button>
                {s.ctaSecondary && (
                  <button
                    onClick={() => navigate(s.ctaSecondary.to)}
                    style={{
                      background: 'transparent',
                      color: '#fff',
                      border: '2px solid rgba(255,255,255,0.65)',
                      padding: isMobile ? '11px 24px' : '13px 34px',
                      fontSize: isMobile ? 13 : 15,
                      fontWeight: 700,
                      borderRadius: 4,
                      cursor: 'pointer',
                      letterSpacing: 0.5,
                      transition: 'border-color 0.2s, background 0.2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = '#fff'; e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.65)'; e.currentTarget.style.background = 'transparent'; }}
                  >
                    {s.ctaSecondary.label}
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Prev / Next arrows — desktop only */}
      {!isMobile && (
        <>
          <button
            onClick={prev}
            aria-label="Previous slide"
            style={{
              position: 'absolute', left: 20, top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(0,0,0,0.25)',
              border: '1.5px solid rgba(255,255,255,0.35)',
              borderRadius: '50%',
              width: 44, height: 44,
              cursor: 'pointer',
              color: '#fff',
              fontSize: 22,
              lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 20,
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.25)'}
          >
            &#8249;
          </button>
          <button
            onClick={next}
            aria-label="Next slide"
            style={{
              position: 'absolute', right: 20, top: '50%',
              transform: 'translateY(-50%)',
              background: 'rgba(0,0,0,0.25)',
              border: '1.5px solid rgba(255,255,255,0.35)',
              borderRadius: '50%',
              width: 44, height: 44,
              cursor: 'pointer',
              color: '#fff',
              fontSize: 22,
              lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              zIndex: 20,
              transition: 'background 0.2s',
            }}
            onMouseEnter={e => e.currentTarget.style.background = 'rgba(0,0,0,0.5)'}
            onMouseLeave={e => e.currentTarget.style.background = 'rgba(0,0,0,0.25)'}
          >
            &#8250;
          </button>
        </>
      )}

      {/* Slide counter — top right */}
      <div style={{
        position: 'absolute', top: 14, right: 16,
        color: 'rgba(255,255,255,0.55)',
        fontSize: 12, fontWeight: 500, letterSpacing: 1,
        zIndex: 20,
      }}>
        {current + 1} / {count}
      </div>

      {/* Dot navigation */}
      <div style={{
        position: 'absolute', bottom: 20, left: 0, right: 0,
        display: 'flex', justifyContent: 'center', alignItems: 'center',
        gap: 8, zIndex: 20,
      }}>
        {SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            style={{
              width: i === current ? 28 : 8,
              height: 8,
              borderRadius: 4,
              background: i === current ? '#D4A017' : 'rgba(255,255,255,0.45)',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
              transition: 'width 0.35s ease, background 0.35s ease',
            }}
          />
        ))}
      </div>

      {/* Progress bar */}
      {!paused && (
        <div style={{
          position: 'absolute', bottom: 0, left: 0,
          height: 3,
          background: '#D4A017',
          animation: `progressBar ${AUTOPLAY_MS}ms linear`,
          animationKey: current,
          zIndex: 20,
        }} />
      )}
    </div>
  );
}
